import React, { useState, useEffect } from 'react';
import { 
  Tablet, 
  Youtube, 
  Globe, 
  ShieldCheck, 
  Search, 
  Check, 
  CheckSquare, 
  Filter, 
  X,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import styles from '../CSS/DeviceGallery.module.css';

const DeviceGallery = ({ devices, selectedDeviceUDIDs, toggleDeviceSelection, animationKey, onSelectAll, onClearSelection }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    youtubeRestricted: false,
    youtubeAllowed: false,
    safariRestricted: false,
    safariAllowed: false,
    botiquin: false
  });

  // Determinar si estamos en móvil usando useEffect y window.innerWidth
  const [isMobile, setIsMobile] = useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    
    // Comprobar al inicio
    checkMobile();
    
    // Comprobar al cambiar tamaño de ventana
    window.addEventListener('resize', checkMobile);
    
    // Limpiar listener
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Comentamos este useEffect para evitar conflictos con el componente padre
  // useEffect(() => {
  //   // Si animationKey cambia, significa que se cambió de clase o grupo
  //   // Limpiar la selección actual
  //   if (onClearSelection) {
  //     onClearSelection();
  //   }
  // }, [animationKey, onClearSelection]);

  // Manejar cambio en los filtros
  const handleFilterChange = (filterName) => {
    setFilters({
      ...filters,
      [filterName]: !filters[filterName]
    });
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    setFilters({
      youtubeRestricted: false,
      youtubeAllowed: false,
      safariRestricted: false,
      safariAllowed: false,
      botiquin: false
    });
    setSearchTerm('');
  };
  
  // Función para seleccionar todos los dispositivos visibles
  const handleSelectAll = () => {
    if (!onSelectAll) return;
    
    // Obtener UDIDs de todos los dispositivos visibles según los filtros actuales
    const visibleDeviceUDIDs = filteredDevices.map(device => device.UDID);
    
    // Si todos los dispositivos visibles ya están seleccionados, deseleccionar todos
    const allSelected = visibleDeviceUDIDs.length > 0 && 
      visibleDeviceUDIDs.every(udid => selectedDeviceUDIDs.includes(udid));
    
    if (allSelected) {
      // Deseleccionar todos los visibles
      if (onClearSelection) {
        onClearSelection();
      }
    } else {
      // Seleccionar todos los visibles
      onSelectAll(visibleDeviceUDIDs);
    }
  };

  // Filtrar dispositivos basado en la búsqueda, selección y filtros adicionales
  const filteredDevices = devices.filter(device => {
    // Filtro de búsqueda
    const searchMatch = device.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro de selección
    const selectionMatch = !showOnlySelected || selectedDeviceUDIDs.includes(device.UDID);
    
    // Comprobar estado de restricciones
    const isYoutubeRestricted = device.groupIds && device.groupIds.includes('266');
    const isSafariRestricted = device.groupIds && device.groupIds.includes('133');
    const isBotiquin = device.groupIds && device.groupIds.includes('130');
    
    // Aplicar filtros adicionales activos
    let filterMatch = true;
    
    // Si hay algún filtro activo, verificar coincidencias
    const hasActiveFilters = Object.values(filters).some(value => value);
    
    if (hasActiveFilters) {
      filterMatch = (
        (filters.youtubeRestricted && isYoutubeRestricted) ||
        (filters.youtubeAllowed && !isYoutubeRestricted) ||
        (filters.safariRestricted && isSafariRestricted) ||
        (filters.safariAllowed && !isSafariRestricted) ||
        (filters.botiquin && isBotiquin)
      );
    }
    
    return searchMatch && selectionMatch && filterMatch;
  });

  // Agrupar dispositivos por tipo de restricción para mejorar visualización
  const categorizedDevices = {
    bothRestricted: [],
    youtubeRestricted: [],
    safariRestricted: [],
    noRestrictions: []
  };

  filteredDevices.forEach(device => {
    const isYoutubeRestricted = device.groupIds && device.groupIds.includes('266');
    const isSafariRestricted = device.groupIds && device.groupIds.includes('133');
    
    if (isYoutubeRestricted && isSafariRestricted) {
      categorizedDevices.bothRestricted.push(device);
    } else if (isYoutubeRestricted) {
      categorizedDevices.youtubeRestricted.push(device);
    } else if (isSafariRestricted) {
      categorizedDevices.safariRestricted.push(device);
    } else {
      categorizedDevices.noRestrictions.push(device);
    }
  });

  // Reordenar para mostrar primero los restringidos
  const sortedDevices = [
    ...categorizedDevices.bothRestricted,
    ...categorizedDevices.youtubeRestricted,
    ...categorizedDevices.safariRestricted,
    ...categorizedDevices.noRestrictions
  ];

  // Obtener contador de estados
  const getStatusCount = () => {
    return {
      total: devices.length,
      selected: selectedDeviceUDIDs.length,
      filtered: filteredDevices.length,
      youtubeRestricted: devices.filter(d => d.groupIds && d.groupIds.includes('266')).length,
      safariRestricted: devices.filter(d => d.groupIds && d.groupIds.includes('133')).length,
      botiquin: devices.filter(d => d.groupIds && d.groupIds.includes('130')).length
    };
  };

  const statusCount = getStatusCount();
  
  // Verificar si todos los dispositivos visibles están seleccionados
  const allVisibleSelected = filteredDevices.length > 0 && 
    filteredDevices.every(device => selectedDeviceUDIDs.includes(device.UDID));

  // Renderizado del dispositivo
  const renderDevice = (device) => {
    const isYoutubeRestricted = device.groupIds && device.groupIds.includes('266');
    const isSafariRestricted = device.groupIds && device.groupIds.includes('133');
    const isBotiquin = device.groupIds && device.groupIds.includes('130');
    
    const displayName = device.name.replace(/\biPad\b/gi, '').trim() || 'Sin Asignar';
    
    return (
      <div
        key={device.UDID}
        className={`${styles.deviceCard} ${selectedDeviceUDIDs.includes(device.UDID) ? styles.selected : ''}`}
        onClick={() => toggleDeviceSelection(device.UDID)}
      >
        {isBotiquin && (
          <div className={styles.botiquinBadge} title="Botiquín">
            <ShieldCheck size={16} />
          </div>
        )}
        
        <div className={styles.deviceIconContainer}>
          <Tablet size={36} className={styles.deviceIcon} />
        </div>
        
        <p className={styles.deviceName}>{displayName}</p>
        
        <div className={styles.restrictionContainer}>
          <div className={`${styles.restrictionStatus} ${isYoutubeRestricted ? styles.youtubeRestricted : styles.youtubeAllowed}`}>
            <Youtube size={16} />
            <span>{isYoutubeRestricted ? 'Restringido' : 'Permitido'}</span>
          </div>
          
          <div className={`${styles.restrictionStatus} ${isSafariRestricted ? styles.safariRestricted : styles.safariAllowed}`}>
            <Globe size={16} />
            <span>{isSafariRestricted ? 'Restringido' : 'Permitido'}</span>
          </div>
        </div>
        
        <div className={styles.selectionIndicator}>
          {selectedDeviceUDIDs.includes(device.UDID) ? 
            <CheckCircle2 size={18} /> : 
            <div className={styles.selectIndicatorEmpty} />
          }
        </div>
      </div>
    );
  };

  // Renderizado diferente para móvil y desktop
  return (
    <div className={styles.deviceGalleryContainer} key={animationKey}>
      {isMobile ? (
        // Layout para móvil
        <>
          <div className={styles.titleSection}>
            <h3 className={styles.galleryTitle}>Dispositivos</h3>
            <div className={styles.galleryStats}>
              <span className={styles.statItem}>
                <span className={styles.statValue}>{statusCount.filtered}</span> mostrados
              </span>
              <span className={styles.statDivider}>/</span>
              <span className={styles.statItem}>
                <span className={styles.statValue}>{statusCount.selected}</span> seleccionados
              </span>
              <span className={styles.statDivider}>/</span>
              <span className={styles.statItem}>
                <span className={styles.statValue}>{statusCount.total}</span> total
              </span>
            </div>
          </div>
          
          <div className={styles.mobileSearchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar dispositivo..."
              className={styles.searchInput}
            />
            {searchTerm && (
              <button 
                className={styles.clearSearchButton}
                onClick={() => setSearchTerm('')}
                title="Limpiar búsqueda"
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          <div className={styles.mobileActionButtons}>  
            <button 
              className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              title="Mostrar filtros"
            >
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            
            <button 
              className={`${styles.selectionButton} ${showOnlySelected ? styles.active : ''}`}
              onClick={() => setShowOnlySelected(!showOnlySelected)}
              title={showOnlySelected ? "Mostrar todos" : "Mostrar seleccionados"}
            >
              <CheckSquare size={16} />
              <span>{selectedDeviceUDIDs.length}</span>
            </button>
            
            <button 
              className={`${styles.selectAllButton} ${allVisibleSelected ? styles.active : ''}`}
              onClick={handleSelectAll}
              title={allVisibleSelected ? "Desmarcar todos" : "Seleccionar todos"}
              disabled={filteredDevices.length === 0}
            >
              <Check size={16} />
              <span>Todos</span>
            </button>
          </div>
          
          {showFilters && (
            <div className={styles.filtersPanel}>
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>YouTube</span>
                <button
                  className={`${styles.filterChip} ${filters.youtubeRestricted ? styles.active : ''}`}
                  onClick={() => handleFilterChange('youtubeRestricted')}
                >
                  <XCircle size={14} className={styles.chipIcon} />
                  <span>Restringido</span>
                  <span className={styles.chipCount}>{statusCount.youtubeRestricted}</span>
                </button>
                <button
                  className={`${styles.filterChip} ${filters.youtubeAllowed ? styles.active : ''}`}
                  onClick={() => handleFilterChange('youtubeAllowed')}
                >
                  <CheckCircle2 size={14} className={styles.chipIcon} />
                  <span>Permitido</span>
                  <span className={styles.chipCount}>{statusCount.total - statusCount.youtubeRestricted}</span>
                </button>
              </div>
              
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>Safari</span>
                <button
                  className={`${styles.filterChip} ${filters.safariRestricted ? styles.active : ''}`}
                  onClick={() => handleFilterChange('safariRestricted')}
                >
                  <XCircle size={14} className={styles.chipIcon} />
                  <span>Restringido</span>
                  <span className={styles.chipCount}>{statusCount.safariRestricted}</span>
                </button>
                <button
                  className={`${styles.filterChip} ${filters.safariAllowed ? styles.active : ''}`}
                  onClick={() => handleFilterChange('safariAllowed')}
                >
                  <CheckCircle2 size={14} className={styles.chipIcon} />
                  <span>Permitido</span>
                  <span className={styles.chipCount}>{statusCount.total - statusCount.safariRestricted}</span>
                </button>
              </div>
              
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>Otros</span>
                <button
                  className={`${styles.filterChip} ${filters.botiquin ? styles.active : ''}`}
                  onClick={() => handleFilterChange('botiquin')}
                >
                  <ShieldCheck size={14} className={styles.chipIcon} />
                  <span>Botiquín</span>
                  <span className={styles.chipCount}>{statusCount.botiquin}</span>
                </button>
              </div>
              
              {Object.values(filters).some(f => f) && (
                <button 
                  className={styles.clearFiltersButton}
                  onClick={clearFilters}
                  title="Limpiar filtros"
                >
                  <X size={14} />
                  <span>Limpiar filtros</span>
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        // Layout original para desktop
        <div className={styles.galleryHeader}>
          <div className={styles.titleSection}>
            <h3 className={styles.galleryTitle}>Dispositivos</h3>
            <div className={styles.galleryStats}>
              <span className={styles.statItem}>
                <span className={styles.statValue}>{statusCount.filtered}</span> mostrados
              </span>
              <span className={styles.statDivider}>/</span>
              <span className={styles.statItem}>
                <span className={styles.statValue}>{statusCount.selected}</span> seleccionados
              </span>
              <span className={styles.statDivider}>/</span>
              <span className={styles.statItem}>
                <span className={styles.statValue}>{statusCount.total}</span> total
              </span>
            </div>
          </div>
          
          <div className={styles.galleryControls}>
            <div className={styles.searchBox}>
              <Search size={16} className={styles.searchIcon} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar dispositivo..."
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearSearchButton}
                  onClick={() => setSearchTerm('')}
                  title="Limpiar búsqueda"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          
          <div className={styles.actionButtons}>  
            <button 
              className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              title="Mostrar filtros"
            >
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            
            <button 
              className={`${styles.selectionButton} ${showOnlySelected ? styles.active : ''}`}
              onClick={() => setShowOnlySelected(!showOnlySelected)}
              title={showOnlySelected ? "Mostrar todos" : "Mostrar seleccionados"}
            >
              <CheckSquare size={16} />
              <span>{selectedDeviceUDIDs.length}</span>
            </button>
            
            <button 
              className={`${styles.selectAllButton} ${allVisibleSelected ? styles.active : ''}`}
              onClick={handleSelectAll}
              title={allVisibleSelected ? "Desmarcar todos" : "Seleccionar todos"}
              disabled={filteredDevices.length === 0}
            >
              <Check size={16} />
              <span>Seleccionar todos</span>
            </button>
          </div>
        </div>
      )}
      
      {showFilters && !isMobile && (
        <div className={styles.filtersPanel}>
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>YouTube</span>
            <button
              className={`${styles.filterChip} ${filters.youtubeRestricted ? styles.active : ''}`}
              onClick={() => handleFilterChange('youtubeRestricted')}
            >
              <XCircle size={14} className={styles.chipIcon} />
              <span>Restringido</span>
              <span className={styles.chipCount}>{statusCount.youtubeRestricted}</span>
            </button>
            <button
              className={`${styles.filterChip} ${filters.youtubeAllowed ? styles.active : ''}`}
              onClick={() => handleFilterChange('youtubeAllowed')}
            >
              <CheckCircle2 size={14} className={styles.chipIcon} />
              <span>Permitido</span>
              <span className={styles.chipCount}>{statusCount.total - statusCount.youtubeRestricted}</span>
            </button>
          </div>
          
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Safari</span>
            <button
              className={`${styles.filterChip} ${filters.safariRestricted ? styles.active : ''}`}
              onClick={() => handleFilterChange('safariRestricted')}
            >
              <XCircle size={14} className={styles.chipIcon} />
              <span>Restringido</span>
              <span className={styles.chipCount}>{statusCount.safariRestricted}</span>
            </button>
            <button
              className={`${styles.filterChip} ${filters.safariAllowed ? styles.active : ''}`}
              onClick={() => handleFilterChange('safariAllowed')}
            >
              <CheckCircle2 size={14} className={styles.chipIcon} />
              <span>Permitido</span>
              <span className={styles.chipCount}>{statusCount.total - statusCount.safariRestricted}</span>
            </button>
          </div>
          
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Otros</span>
            <button
              className={`${styles.filterChip} ${filters.botiquin ? styles.active : ''}`}
              onClick={() => handleFilterChange('botiquin')}
            >
              <ShieldCheck size={14} className={styles.chipIcon} />
              <span>Botiquín</span>
              <span className={styles.chipCount}>{statusCount.botiquin}</span>
            </button>
          </div>
          
          {Object.values(filters).some(f => f) && (
            <button 
              className={styles.clearFiltersButton}
              onClick={clearFilters}
              title="Limpiar filtros"
            >
              <X size={14} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>
      )}
      
      <div className={styles.deviceGrid}>
        {sortedDevices.length > 0 ? (
          sortedDevices.map(device => renderDevice(device))
        ) : (
          <div className={styles.emptyState}>
            <AlertTriangle size={36} className={styles.emptyIcon} />
            <p className={styles.emptyText}>
              {searchTerm || Object.values(filters).some(f => f) ? 
                'No se encontraron dispositivos con los criterios seleccionados' : 
                'No hay dispositivos disponibles'
              }
            </p>
            {(searchTerm || Object.values(filters).some(f => f)) && (
              <button 
                onClick={clearFilters}
                className={styles.resetButton}
              >
                Restablecer filtros
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceGallery;